// Main LoggingService for the AlphaMind IDO Frontend logging system
import {
    LogAction,
    LogCategory,
    LogContext,
    LogEvent,
    LogEventType,
    LoggingConfig,
    WalletType
} from '../types/logging.types';
import { BatchLoggerService } from './batch-logger.service';
import {
    generateLogId,
    generateSessionId,
    getCurrentTimestamp,
    getDeviceInfo,
    getNetworkInfo,
    getPerformanceInfo,
    getUrlInfo,
    getWalletInfo,
    parseStringArray,
    shouldEnableLogging
} from '../utils/device-detection';
import { getAppBuildInfo } from '../utils/app-build-info';

// Singleton instance
let loggingServiceInstance: LoggingService | null = null;

export class LoggingService {
    private batchLogger: BatchLoggerService;
    private sessionId: string;
    private isEnabled: boolean;

    constructor(config: LoggingConfig) {
        this.sessionId = generateSessionId();
        this.isEnabled = config.enabled && shouldEnableLogging();
        this.batchLogger = new BatchLoggerService(config);

        // Make service available globally for debugging
        if (typeof window !== 'undefined') {
            (window as any).__logging_service = this;
        }
    }

    // Core logging method
    public async log(event: Partial<LogEvent>): Promise<void> {
        if (!this.isEnabled) return;

        try {
            const context = await this.buildContext();
            const currentWalletAddress = await this.getCurrentWalletAddress();

            const logEvent: LogEvent = {
                id: generateLogId(),
                sessionId: this.sessionId,
                timestamp: getCurrentTimestamp(),
                eventType: event.eventType || LogEventType.USER_ACTION,
                category: event.category || LogCategory.USER_INTERACTION,
                action: event.action || 'unknown',
                userId: event.userId,
                walletAddress: event.walletAddress || currentWalletAddress,
                data: event.data,
                error: event.error,
                context
            };

            this.batchLogger.addLog(logEvent);

            // Console logging in development
            if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_LOG_CONSOLE_ENABLED === 'true') {
                console.log('Log Event:', logEvent);
            }

        } catch (error) {
            console.error('Failed to create log event:', error);
        }
    }

    // Wallet connection logging methods
    public logWalletConnection(walletType: WalletType, action: LogAction, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.WALLET_CONNECTION,
            category: LogCategory.CONNECTION,
            action,
            data: {
                walletType,
                ...data
            }
        });
    }

    public logConnectionAttempt(walletType: WalletType, data?: Record<string, any>): void {
        this.logWalletConnection(walletType, LogAction.CONNECTION_ATTEMPT, data);
    }

    public logConnectionSuccess(walletType: WalletType, address?: string, data?: Record<string, any>): void {
        this.logWalletConnection(walletType, LogAction.CONNECTION_SUCCESS, {
            walletAddress: address,
            ...data
        });
    }

    public logConnectionError(walletType: WalletType, error: Error, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.WALLET_CONNECTION,
            category: LogCategory.CONNECTION,
            action: LogAction.CONNECTION_ERROR,
            error: {
                message: error.message,
                stack: error.stack,
                name: error.name
            },
            data: {
                walletType,
                ...data
            }
        });
    }

    public logProviderConflict(conflictingProviders: string[], data?: Record<string, any>): void {
        this.logWalletConnection('unknown', LogAction.PROVIDER_CONFLICT, {
            conflictingProviders,
            ...data
        });
    }

    // Authentication logging methods
    public logAuthentication(action: LogAction, walletType?: WalletType, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.AUTHENTICATION,
            category: LogCategory.AUTH,
            action,
            data: {
                walletType,
                ...data
            }
        });
    }

    public logLoginAttempt(walletType: WalletType, data?: Record<string, any>): void {
        this.logAuthentication(LogAction.LOGIN_ATTEMPT, walletType, data);
    }

    public logLoginSuccess(walletType: WalletType, userId: string, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.AUTHENTICATION,
            category: LogCategory.AUTH,
            action: LogAction.LOGIN_SUCCESS,
            userId,
            data: {
                walletType,
                ...data
            }
        });
    }

    public logLoginError(walletType: WalletType, error: Error, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.AUTHENTICATION,
            category: LogCategory.AUTH,
            action: LogAction.LOGIN_ERROR,
            error: {
                message: error.message,
                stack: error.stack,
                name: error.name
            },
            data: {
                walletType,
                ...data
            }
        });
    }

    public logMessageSign(walletType: WalletType, success: boolean, data?: Record<string, any>): void {
        this.logAuthentication(LogAction.MESSAGE_SIGN, walletType, {
            success,
            ...data
        });
    }

    public logNetworkSwitch(fromChainId: number, toChainId: number, success: boolean, data?: Record<string, any>): void {
        this.logAuthentication(LogAction.NETWORK_SWITCH, undefined, {
            fromChainId,
            toChainId,
            success,
            ...data
        });
    }

    // IDO interaction logging methods
    public logIdoInteraction(action: LogAction, idoId: string, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.IDO_INTERACTION,
            category: LogCategory.NAVIGATION,
            action,
            data: {
                idoId,
                ...data
            }
        });
    }

    public logIdoPageView(idoId: string, idoTitle: string, data?: Record<string, any>): void {
        this.logIdoInteraction(LogAction.IDO_PAGE_VIEW, idoId, {
            idoTitle,
            ...data
        });
    }

    public logIdoDetailsView(idoId: string, section: string, data?: Record<string, any>): void {
        this.logIdoInteraction(LogAction.IDO_DETAILS_VIEW, idoId, {
            section,
            ...data
        });
    }

    public logWhitelistAttempt(idoId: string, data?: Record<string, any>): void {
        this.logIdoInteraction(LogAction.WHITELIST_ATTEMPT, idoId, data);
    }

    public logWhitelistSuccess(idoId: string, data?: Record<string, any>): void {
        this.logIdoInteraction(LogAction.WHITELIST_SUCCESS, idoId, data);
    }

    public logWhitelistError(idoId: string, error: Error, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.IDO_INTERACTION,
            category: LogCategory.NAVIGATION,
            action: LogAction.WHITELIST_ERROR,
            error: {
                message: error.message,
                stack: error.stack,
                name: error.name
            },
            data: {
                idoId,
                ...data
            }
        });
    }

    // Purchase logging methods
    public logPurchase(action: LogAction, idoId: string, amount: string, currency: string, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.PURCHASE,
            category: LogCategory.TRANSACTION,
            action,
            data: {
                idoId,
                amount,
                currency,
                ...data
            }
        });
    }

    public logPurchaseAttempt(idoId: string, amount: string, currency: string, data?: Record<string, any>): void {
        this.logPurchase(LogAction.PURCHASE_ATTEMPT, idoId, amount, currency, data);
    }

    public logPurchaseSuccess(idoId: string, amount: string, currency: string, txHash: string, data?: Record<string, any>): void {
        this.logPurchase(LogAction.PURCHASE_SUCCESS, idoId, amount, currency, {
            txHash,
            ...data
        });
    }

    public logPurchaseError(idoId: string, amount: string, currency: string, error: Error, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.PURCHASE,
            category: LogCategory.TRANSACTION,
            action: LogAction.PURCHASE_ERROR,
            error: {
                message: error.message,
                stack: error.stack,
                name: error.name
            },
            data: {
                idoId,
                amount,
                currency,
                ...data
            }
        });
    }

    public logPurchaseCancel(idoId: string, reason: string, data?: Record<string, any>): void {
        this.logPurchase(LogAction.PURCHASE_CANCEL, idoId, '', '', {
            reason,
            ...data
        });
    }

    // Error logging methods
    public logError(action: LogAction, error: Error, component?: string, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.ERROR,
            category: LogCategory.ERROR,
            action,
            error: {
                message: error.message,
                stack: error.stack,
                name: error.name
            },
            data: {
                component,
                errorType: error.constructor.name,
                ...data
            }
        });
    }

    public logJavaScriptError(error: Error, component?: string, action?: string, data?: Record<string, any>): void {
        this.logError(LogAction.JAVASCRIPT_ERROR, error, component, {
            action,
            ...data
        });
    }

    public logValidationError(error: Error, field: string, value: any, data?: Record<string, any>): void {
        this.logError(LogAction.VALIDATION_ERROR, error, undefined, {
            field,
            value,
            ...data
        });
    }

    public logNetworkError(error: Error, endpoint: string, data?: Record<string, any>): void {
        this.logError(LogAction.NETWORK_ERROR, error, undefined, {
            endpoint,
            ...data
        });
    }

    // Page view logging methods
    public logPageView(path?: string, title?: string, data?: Record<string, any>): void {
        const urlInfo = getUrlInfo();

        this.log({
            eventType: LogEventType.PAGE_VIEW,
            category: LogCategory.NAVIGATION,
            action: LogAction.PAGE_LOAD,
            data: {
                path: path || urlInfo.pathname,
                title: title || document.title,
                url: urlInfo.url,
                referrer: urlInfo.referrer,
                ...data
            }
        });
    }

    public logRouteChange(from: string, to: string, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.PAGE_VIEW,
            category: LogCategory.NAVIGATION,
            action: LogAction.ROUTE_CHANGE,
            data: {
                from,
                to,
                ...data
            }
        });
    }

    // User action logging methods
    public logUserAction(action: LogAction, component: string, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.USER_ACTION,
            category: LogCategory.USER_INTERACTION,
            action,
            data: {
                component,
                ...data
            }
        });
    }

    public logButtonClick(buttonId: string, component: string, data?: Record<string, any>): void {
        this.logUserAction(LogAction.BUTTON_CLICK, component, {
            buttonId,
            ...data
        });
    }

    public logFormSubmit(formId: string, component: string, data?: Record<string, any>): void {
        this.logUserAction(LogAction.FORM_SUBMIT, component, {
            formId,
            ...data
        });
    }

    public logModalOpen(modalId: string, trigger: string, data?: Record<string, any>): void {
        this.logUserAction(LogAction.MODAL_OPEN, 'modal', {
            modalId,
            trigger,
            ...data
        });
    }

    public logModalClose(modalId: string, trigger: string, data?: Record<string, any>): void {
        this.logUserAction(LogAction.MODAL_CLOSE, 'modal', {
            modalId,
            trigger,
            ...data
        });
    }

    // Performance logging methods
    public logPerformance(metric: string, value: number, unit: string, component?: string, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.PERFORMANCE,
            category: LogCategory.PERFORMANCE,
            action: 'custom_metric',
            data: {
                metric,
                value,
                unit,
                component,
                ...data
            }
        });
    }

    public logComponentMount(componentName: string, mountTime: number, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.PERFORMANCE,
            category: LogCategory.PERFORMANCE,
            action: LogAction.COMPONENT_MOUNT,
            data: {
                componentName,
                mountTime,
                ...data
            }
        });
    }

    public logComponentUnmount(componentName: string, lifetime: number, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.PERFORMANCE,
            category: LogCategory.PERFORMANCE,
            action: LogAction.COMPONENT_UNMOUNT,
            data: {
                componentName,
                lifetime,
                ...data
            }
        });
    }

    public logApiRequest(endpoint: string, method: string, duration: number, success: boolean, data?: Record<string, any>): void {
        this.log({
            eventType: LogEventType.PERFORMANCE,
            category: LogCategory.PERFORMANCE,
            action: success ? LogAction.API_RESPONSE : LogAction.API_REQUEST,
            data: {
                endpoint,
                method,
                duration,
                success,
                ...data
            }
        });
    }

    // Utility methods
    public async flush(): Promise<void> {
        await this.batchLogger.flush();
    }

    public getSessionId(): string {
        return this.sessionId;
    }

    public isLoggingEnabled(): boolean {
        return this.isEnabled;
    }

    // Get current connected wallet address
    private async getCurrentWalletAddress(): Promise<string | undefined> {
        try {
            // Try to get address from wagmi store (most reliable for Ethereum wallets)
            if (typeof window !== 'undefined' && (window as any).__wagmi_store) {
                try {
                    const wagmiStore = (window as any).__wagmi_store;
                    const state = wagmiStore.getState();
                    if (state?.account?.address) {
                        return state.account.address;
                    }
                } catch (error) {
                    // wagmi store not available or error
                }
            }

            // Try to get Ethereum wallet address directly
            if (typeof window !== 'undefined' && (window as any).ethereum) {
                try {
                    const accounts = await (window as any).ethereum.request({
                        method: 'eth_accounts'
                    });
                    if (accounts && accounts.length > 0) {
                        return accounts[0];
                    }
                } catch (error) {
                    // Ethereum wallet not connected or error
                }
            }

            // Try to get Solana wallet address from Phantom
            if (typeof window !== 'undefined' && (window as any).solana) {
                try {
                    const solanaWallet = (window as any).solana;
                    if (solanaWallet.isConnected && solanaWallet.publicKey) {
                        return solanaWallet.publicKey.toString();
                    }
                } catch (error) {
                    // Solana wallet not connected or error
                }
            }

            // Try to get address from Solana wallet adapter context
            if (typeof window !== 'undefined' && (window as any).__solana_wallet_adapter) {
                try {
                    const solanaAdapter = (window as any).__solana_wallet_adapter;
                    if (solanaAdapter.connected && solanaAdapter.publicKey) {
                        return solanaAdapter.publicKey.toString();
                    }
                } catch (error) {
                    // Solana adapter not available or error
                }
            }

            // Try to get address from localStorage (fallback for persisted connections)
            if (typeof window !== 'undefined' && window.localStorage) {
                try {
                    const storedAddress = localStorage.getItem('walletAddress') ||
                        localStorage.getItem('connectedWallet') ||
                        localStorage.getItem('wallet_address') ||
                        localStorage.getItem('wagmi.account') ||
                        localStorage.getItem('solana.wallet.address');
                    if (storedAddress) {
                        // Try to parse JSON in case it's stored as an object
                        try {
                            const parsed = JSON.parse(storedAddress);
                            return parsed.address || parsed.publicKey || parsed;
                        } catch {
                            return storedAddress;
                        }
                    }
                } catch (error) {
                    // localStorage not available or error
                }
            }

            return undefined;
        } catch (error) {
            console.warn('Error getting current wallet address for logging:', error);
            return undefined;
        }
    }

    // Build context for log events
    private async buildContext(): Promise<LogContext> {
        const urlInfo = getUrlInfo();
        const deviceInfo = getDeviceInfo();
        const networkInfo = await getNetworkInfo();
        const walletInfo = getWalletInfo();
        const performanceInfo = getPerformanceInfo();

        return {
            url: urlInfo.url,
            referrer: urlInfo.referrer,
            userAgent: navigator.userAgent,
            app: getAppBuildInfo(),
            device: deviceInfo,
            network: networkInfo,
            wallet: walletInfo,
            performance: performanceInfo
        };
    }

    // Force immediate flush of pending logs (for critical events like wallet changes)
    // Made completely non-blocking to prevent page loading issues
    public async forceFlush(): Promise<void> {
        this.batchLogger.forceFlush().catch(error => {
            console.warn('Force flush failed silently (page loading continues):', error);
        });
    }

    // Check if there are pending logs
    public hasPendingLogs(): boolean {
        return this.batchLogger.hasPendingLogs();
    }

    // Cleanup resources
    public destroy(): void {
        this.batchLogger.destroy();

        if (typeof window !== 'undefined') {
            delete (window as any).__logging_service;
        }
    }
}

// Configuration factory
export const createLoggingConfig = (): LoggingConfig => {
    return {
        endpoint: `${process.env.REACT_APP_API_SECONDARY}${process.env.REACT_APP_LOGGING_ENDPOINT}` || '/api/logs/batch',
        batchSize: parseInt(process.env.REACT_APP_LOG_BATCH_SIZE || '50'),
        flushInterval: parseInt(process.env.REACT_APP_LOG_FLUSH_INTERVAL || '30000'),
        enabled: process.env.REACT_APP_ENABLE_LOGGING !== 'false',
        enabledCategories: parseStringArray(process.env.REACT_APP_LOG_ENABLED_CATEGORIES) as LogCategory[],
        enabledEventTypes: parseStringArray(process.env.REACT_APP_LOG_ENABLED_EVENT_TYPES) as LogEventType[],
        consoleEnabled: process.env.REACT_APP_LOG_CONSOLE_ENABLED === 'true',
        retryAttempts: parseInt(process.env.REACT_APP_LOG_RETRY_ATTEMPTS || '3'),
        retryDelay: parseInt(process.env.REACT_APP_LOG_RETRY_DELAY || '1000')
    };
};

// Singleton factory
export const getLoggingService = (): LoggingService => {
    if (!loggingServiceInstance) {
        const config = createLoggingConfig();
        loggingServiceInstance = new LoggingService(config);
    }
    return loggingServiceInstance;
};

// Initialize logging service
export const initializeLoggingService = (config?: Partial<LoggingConfig>): LoggingService => {
    const fullConfig = { ...createLoggingConfig(), ...config };
    loggingServiceInstance = new LoggingService(fullConfig);
    return loggingServiceInstance;
};
